import type { JSX } from 'react';

import { TFunction } from '@mint-lib/i18n';

import { Button, IconButton, Tooltip } from '@myn/mui';

import { getTooltipMessage } from '../../constants/myCampaigns.js';
import { Co2Order, Co2OrderStatus } from '../../types/co2notifications.js';

type RenderButtonsOptions = {
  status: Co2OrderStatus;
  item: Co2Order;
  onReject: () => void;
  onAccept: () => void;
  onDownloadButtonClick: () => void;
  onWebsiteButtonClick: () => void;
  readOnlyUser?: boolean;
  co2ReadOnly: boolean;
  isCurrencyConverted?: boolean;
};

// TODO move to theme
export const mapStatusToBg: Record<Co2OrderStatus, string> = {
  [Co2OrderStatus.Invoiced]: '#edfedf',
  [Co2OrderStatus.Booked]: '#edfedf',
  [Co2OrderStatus.Draft]: '#e1e1e1',
  [Co2OrderStatus.Rejected]: '#ffe6e0',
};

export const mapStatusToTextColor: Record<Co2OrderStatus, string> = {
  [Co2OrderStatus.Invoiced]: '#068509',
  [Co2OrderStatus.Booked]: '#068509',
  [Co2OrderStatus.Draft]: '#333333',
  [Co2OrderStatus.Rejected]: '#ff566a',
};

export const SERVER_DATE_FORMAT = 'yyyy-MM-dd' as const;
export const CLIENT_DATE_FORMAT = 'dd/MM/yyyy' as const;

export const useMapStatusToDescription = (
  t: TFunction,
): Record<Co2OrderStatus, string> => {
  return {
    [Co2OrderStatus.Booked]: t(
      "Your buying plan's carbon emissions were offset",
    ),
    [Co2OrderStatus.Rejected]: t(
      "Your buying plan's carbon emissions are not offset",
    ),
    [Co2OrderStatus.Draft]: t(
      'Your buying plan has come to an end, and you can now see the finalized cost to offset your carbon emissions. Confirm to offset your carbon emissions.',
    ),
    [Co2OrderStatus.Invoiced]: '',
  };
};

export const useMapStatusToTextMessage = (
  t: TFunction,
): Record<Co2OrderStatus, string> => ({
  [Co2OrderStatus.Booked]: t(
    'Service provided in collaboration with ClimatePartner',
  ),
  [Co2OrderStatus.Draft]: t(
    'Service provided in collaboration with ClimatePartner',
  ),
  [Co2OrderStatus.Invoiced]: '',
  [Co2OrderStatus.Rejected]: '',
});

export const mapStatusToButtons = (
  t: TFunction,
): Record<Co2OrderStatus, (options: RenderButtonsOptions) => JSX.Element> => ({
  [Co2OrderStatus.Invoiced]: (options) => (
    <>
      <Tooltip title="Download certificate">
        <div data-testid="certificateButton">
          <IconButton
            icon="DocumentDownload"
            variant="outlined"
            onClick={options.onDownloadButtonClick}
            sx={{ marginRight: 1 }}
          />
        </div>
      </Tooltip>
      <Tooltip title="Get the certificate from website">
        <div data-testid="websiteButton">
          <IconButton
            icon="EarthAmericas"
            variant="outlined"
            onClick={options.onWebsiteButtonClick}
          />
        </div>
      </Tooltip>
    </>
  ),
  [Co2OrderStatus.Booked]: (options) => (
    <>
      <Tooltip title="Download certificate">
        <div data-testid="certificateButton">
          <IconButton
            icon="DocumentDownload"
            variant="outlined"
            onClick={options.onDownloadButtonClick}
            sx={{ marginRight: 1 }}
          />
        </div>
      </Tooltip>
      <Tooltip title="Get the certificate from website">
        <div data-testid="websiteButton">
          <IconButton
            icon="EarthAmericas"
            variant="outlined"
            onClick={options.onWebsiteButtonClick}
          />
        </div>
      </Tooltip>
    </>
  ),
  [Co2OrderStatus.Draft]: (options) => {
    const tooltipMassages = getTooltipMessage(t);
    const tooltip =
      options.readOnlyUser || options.co2ReadOnly
        ? tooltipMassages.CO2_NO_ACCESS
        : null;

    return (
      <>
        <Button
          startIcon="CheckmarkOutline"
          onClick={options.onReject}
          variant="contained"
          size="large"
          color="error"
          disabled={
            options.readOnlyUser ||
            !options.isCurrencyConverted ||
            options.co2ReadOnly
          }
        >
          <Tooltip title={tooltip}>
            <span>{'REJECT'}</span>
          </Tooltip>
        </Button>
        <Button
          startIcon="CheckmarkOutline"
          onClick={options.onAccept}
          variant="contained"
          size="large"
          color="primary"
          disabled={
            options.readOnlyUser ||
            !options.isCurrencyConverted ||
            options.co2ReadOnly
          }
        >
          <Tooltip title={tooltip}>
            <span>{t('ACCEPT')}</span>
          </Tooltip>
        </Button>
      </>
    );
  },
  [Co2OrderStatus.Rejected]: () => (
    <Button startIcon="CheckmarkOutline" size="large" color="error">
      {t('REJECTED')}
    </Button>
  ),
});
